/**
 * DTOs aligned with NestJS agentic_ai_conversations / agentic_ai_conversation_messages.
 * Field names use camelCase on the client; backend may use snake_case — map at the API layer if needed.
 */

import type {
  AgentChatMetadata,
  BqPresentation,
  BqPresentationAxisEncoding,
  BqPresentationChartHint,
  BqPresentationViewId,
  HyperlinkColumnSpec,
  TaskGraphDto,
} from '../../views/ai-agent/ai-agent.types';

import { isBqEncryptedIdColumnKey } from './bq-hyperlink-columns';
import { buildSubmissionBatchCopyFromCounts, formatUniqueJobCodesFromSubmissionRows, isStaleSubmissionBatchDefaultMessage, normalizeSubmissionResultAssistantMetadata, submissionResultHasRows } from './submission-result-preview';
import {
  buildInterviewScheduleBatchCopyFromCounts,
  interviewScheduleResultHasRows,
  normalizeInterviewScheduleResultAssistantMetadata,
} from './interview-schedule-result-preview';
import { extractCandidateMatchingChoicePayload } from './submission-choice.models';
import { formatBqDateScalarForDisplay } from '../../shared/utils/company-date-format.util';

function countBqDisplayRowKeys(row: Record<string, unknown>): number {
  return Object.keys(row).filter((k) => !isBqEncryptedIdColumnKey(k)).length;
}

export type AgenticMessageRole = 'system' | 'user' | 'assistant';

/** Coerce API / DB role values (`1`/`2`, labels) to thread UI roles. */
export function normalizeAgenticMessageRole(value: unknown): AgenticMessageRole {
  if (value === 'user' || value === 'assistant' || value === 'system') {
    return value;
  }
  const n = Number(value);
  if (n === 1) {
    return 'user';
  }
  if (n === 2) {
    return 'assistant';
  }
  if (n === 0 || n === 3) {
    return 'system';
  }
  const s = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (s === 'user' || s === 'assistant' || s === 'system') {
    return s;
  }
  return 'assistant';
}

/** `agentic_ai_conversation_messages.feedback_rating` */
export type MessageFeedbackRating = 0 | 1 | 2;

export const MESSAGE_FEEDBACK_MAX_COMMENT_LENGTH = 500;

export function normalizeMessageFeedbackRating(value: unknown): MessageFeedbackRating {
  const n = Number(value ?? 0);
  if (n === 1) {
    return 1;
  }
  if (n === 2) {
    return 2;
  }
  return 0;
}

/** `agentic_ai_conversation_messages.is_favourite` (`1` = saved). */
export function normalizeMessageIsFavourite(value: unknown): 0 | 1 {
  if (value === 1 || value === '1' || value === true) {
    return 1;
  }
  return 0;
}

export interface AgenticAiConversation {
  /** Encrypted public id (Ceipal `encDec` ciphertext). */
  id: string;
  title: string | null;
  status: string;
  /** API returns pinned as 0/1. */
  pinned?: 0 | 1;
  pinnedOn?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AgenticAiMessage {
  /** Encrypted public id when persisted. */
  id?: string;
  conversationId: string;
  sequence: number;
  role: AgenticMessageRole;
  content: string;
  model?: string | null;
  tokens?: number | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  /** User prompt favourite (`1` = saved). */
  isFavourite?: 0 | 1;
  /** Assistant message feedback (`0` = none, `1` = liked, `2` = disliked). */
  feedbackRating?: MessageFeedbackRating;
  /** Optional dislike comment when `feedbackRating` is `2`. */
  feedbackComment?: string | null;
}

export interface FavouritePromptsListResponse {
  items: AgenticAiMessage[];
  hasMore?: boolean;
}

export interface ApplicantPreviewStats {
  submissions?: number | null;
  clientSubmissions?: number | null;
  pipeline?: number | null;
  interviews?: number | null;
  hires?: number | null;
}

export interface ApplicantPreview {
  encryptedId: string;
  applicantId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  location?: string | null;
  stats?: ApplicantPreviewStats | null;
  /** Parsed resume HTML from Domain Orch (`getParsedDocumentDetails`). */
  resumeContent?: string | null;
}

/** @deprecated Use {@link ApplicantPreview}. */
export type ApplicantBasicInfo = ApplicantPreview;

export interface AgenticAiConversationListResponse {
  items: AgenticAiConversation[];
  /** When API supports paged list (limit/offset). */
  total?: number;
  hasMore?: boolean;
}

/** Sidebar title search (`GET …/conversations?q=…`) — minimal fields only. */
export interface AgenticAiConversationSearchHit {
  id: string;
  title: string | null;
  createdAt?: string;
  /** Used for Gmail-style list dates when present (preferred over `createdAt`). */
  updatedAt?: string;
}

export interface CreateAgenticConversationDto {
  title?: string;
  metadata?: Record<string, unknown>;
}

export interface PaginatedMessagesResponse {
  items: AgenticAiMessage[];
  /** Opaque cursor for the next page (older messages). Null when no more. */
  nextCursor: string | null;
  hasMore: boolean;
}

export interface GetMessagesQuery {
  limit: number;
  cursor?: string;
}

export interface PostUserMessageDto {
  content: string;
  metadata?: Record<string, unknown>;
  /** Client public IP (best-effort); persisted on user message rows. */
  ipAddress?: string;
}

/**
 * Expected shape after POST user message (server persists user + assistant rows).
 * Adjust if Nest returns a different envelope — keep method names stable.
 */
export interface PostUserMessageResponse {
  userMessage: AgenticAiMessage;
  assistantMessage: AgenticAiMessage;
  /** Domain orch: same assistant row updated for BQ paging — UI replaces by id instead of appending. */
  bqPageInPlace?: boolean;
}

/** One-shot handoff after BFF `result` before Domain Orch `getMessages` has the assistant row. */
export interface PendingThreadBootstrap {
  conversationId: string;
  userContent: string;
  assistantContent: string;
  /** Persisted from BFF `result` (e.g. `type: bq` rows/sql) when the API omits metadata on first GET. */
  assistantMetadata?: Record<string, unknown> | null;
  /** Skill slash for the user bubble when the bootstrap row is built before the saved message metadata arrives. */
  userMetadata?: Record<string, unknown> | null;
}

/**
 * If the API returns no assistant message yet, append from the BFF result; if empty, keep server rows only.
 */
export function mergeServerMessagesWithBffBootstrap(
  items: AgenticAiMessage[],
  conversationId: string,
  bootstrap: PendingThreadBootstrap,
): AgenticAiMessage[] {
  const ordered = [...items];
  if (ordered.some((m) => m.role === 'assistant')) {
    return ordered;
  }
  const assistantText = bootstrap.assistantContent.trim();
  const normalizedSubmissionMeta = normalizeSubmissionResultAssistantMetadata(
    bootstrap.assistantMetadata != null &&
      typeof bootstrap.assistantMetadata === 'object' &&
      !Array.isArray(bootstrap.assistantMetadata)
      ? (bootstrap.assistantMetadata as Record<string, unknown>)
      : null,
  );
  const normalizedInterviewMeta =
    normalizedSubmissionMeta == null
      ? normalizeInterviewScheduleResultAssistantMetadata(
          bootstrap.assistantMetadata != null &&
            typeof bootstrap.assistantMetadata === 'object' &&
            !Array.isArray(bootstrap.assistantMetadata)
            ? (bootstrap.assistantMetadata as Record<string, unknown>)
            : null,
        )
      : null;
  const normalizedResultMeta = normalizedSubmissionMeta ?? normalizedInterviewMeta;
  const now = new Date().toISOString();
  let base = [...ordered];
  if (!base.some((m) => m.role === 'user')) {
    const maxSeq = base.length ? Math.max(...base.map((m) => m.sequence)) : 0;
    base.push({
      conversationId,
      sequence: maxSeq + 1,
      role: 'user',
      content: bootstrap.userContent,
      metadata: bootstrap.userMetadata ?? null,
      createdAt: now,
    });
  }
  if (!assistantText && !normalizedResultMeta) {
    return base;
  }
  const maxSeq = Math.max(...base.map((m) => m.sequence));
  const resultHeadline =
    normalizedResultMeta && typeof normalizedResultMeta['message'] === 'string'
      ? normalizedResultMeta['message'].trim()
      : '';
  base.push({
    conversationId,
    sequence: maxSeq + 1,
    role: 'assistant',
    content: resultHeadline || bootstrap.assistantContent,
    metadata: normalizedResultMeta ?? bootstrap.assistantMetadata ?? null,
    createdAt: now,
  });
  return base;
}

/** Matches Nest {@link AgentProgressEvent} streamed over SSE (`event: progress`). */
export type AgentSseProgressPhase =
  | 'preparing'
  | 'paged_fetch'
  | 'loading_context'
  | 'routing'
  | 'running_query'
  | 'fixing_sql';

export interface AgentSseProgressEvent {
  /** Domain Orch uses a fixed set; BFF may emit additional phase names as strings. */
  phase: AgentSseProgressPhase | string;
  message?: string;
}

/** Default labels when BFF sends `phase` without `message` (see BFF `runAgentChat` progress). */
const BFF_PROGRESS_PHASE_LABELS: Record<string, string> = {
  preparing: 'Preparing…',
  routing: 'Validating session',
  loading_context: 'Scanning prompt',
  running_query: 'Calling orchestrator',
  paged_fetch: 'Loading data',
  fixing_sql: 'Fixing query',
  job_choice_needed: 'To rank candidates, I need a job.',
  fetching_jobs: 'Fetching the latest open jobs…',
};

/**
 * Single line for the chat loader: prefer SSE `message`, else mapped `phase`, else title-cased phase.
 */
export function formatBffProgressStatus(
  phase: string | null | undefined,
  message: string | null | undefined,
): string {
  const m = message?.trim();
  if (m) {
    return m;
  }
  const p = phase?.trim();
  if (!p) {
    return '';
  }
  const mapped = BFF_PROGRESS_PHASE_LABELS[p];
  if (mapped) {
    return mapped;
  }
  return p
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Final `event: result` payload from BFF `POST …/agent/chat/:conversationId` (SSE).
 * BFF may send camelCase or snake_case; the client normalizes {@link resolveBffChatConversationId}.
 */
/** Nested orchestrator payload (e.g. Meta clarify step) merged onto the SSE `result` event. */
export interface AgentBffChatResultBlock {
  type?: string;
  question?: string;
  /** Main-prompt clarify purpose (`chat`, `greeting`, `product_info`, …). */
  purpose?: string;
  options?: unknown[];
  suggestions?: unknown[];
  [key: string]: unknown;
}

export interface AgentBffChatSseResult {
  conversationId?: string;
  conversation_id?: string;
  reply?: string;
  message?: string;
  content?: string;
  text?: string;
  answer?: string;
  /** Additive standard envelope (BFF / Domain metadata parity). */
  envelopeVersion?: number;
  outcome?: 'ok' | 'domain_error' | 'validation_error' | 'upstream_error';
  displayText?: string;
  resultKind?: string;
  /** Domain execute path: `single_agent` emails are sent server-side (no compose draft bubble). */
  executionType?: string;
  execution_type?: string;
  /** Orchestrator often nests clarify / tool output here (see {@link extractBffChatReply}). */
  result?: AgentBffChatResultBlock | string | null;
  suggestions?: unknown[];
  [key: string]: unknown;
}

/** Maps Domain/BFF persisted turn rows to UI thread messages (encrypted ids + `createdAt`). */
export function normalizeAgenticAiMessageFromApi(raw: unknown): AgenticAiMessage | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const idRaw = o['id'] ?? o['message_id'] ?? o['messageId'];
  const id = idRaw != null && String(idRaw).trim() ? String(idRaw).trim() : undefined;
  const cidRaw = o['conversationId'] ?? o['conversation_id'];
  const conversationId = cidRaw != null && String(cidRaw).trim() ? String(cidRaw).trim() : '';
  const roleRaw = o['role'];
  const role =
    roleRaw === 'user' || roleRaw === 'assistant' || roleRaw === 'system' ? roleRaw : null;
  if (!role) {
    return null;
  }
  const createdRaw = o['createdAt'] ?? o['created_at'];
  let createdAt: string | undefined;
  if (createdRaw instanceof Date) {
    createdAt = createdRaw.toISOString();
  } else if (createdRaw != null && String(createdRaw).trim()) {
    createdAt = String(createdRaw).trim();
  }
  const seq = Number(o['sequence']);
  const isFavourite = normalizeMessageIsFavourite(o['isFavourite'] ?? o['is_favourite']);
  const feedbackRating = normalizeMessageFeedbackRating(
    o['feedbackRating'] ?? o['feedback_rating'],
  );
  const commentRaw = o['feedbackComment'] ?? o['feedback_comment'];
  const feedbackComment =
    commentRaw != null && String(commentRaw).trim() ? String(commentRaw).trim() : null;
  return {
    id,
    conversationId,
    sequence: Number.isFinite(seq) ? seq : 0,
    role,
    content: String(o['content'] ?? ''),
    model: o['model'] != null ? String(o['model']) : null,
    metadata:
      o['metadata'] != null && typeof o['metadata'] === 'object' && !Array.isArray(o['metadata'])
        ? (o['metadata'] as Record<string, unknown>)
        : null,
    createdAt,
    isFavourite,
    feedbackRating,
    feedbackComment,
  };
}

export interface BffChatPersistedTurn {
  userMessage: AgenticAiMessage;
  assistantMessage?: AgenticAiMessage;
}

/** Server-persisted rows from Domain `POST /domain-orch/execute` (forwarded by BFF `event: result`). */
export function extractBffChatPersistedTurn(res: AgentBffChatSseResult): BffChatPersistedTurn | null {
  const o = res as Record<string, unknown>;
  const nested =
    o['result'] != null && typeof o['result'] === 'object' && !Array.isArray(o['result'])
      ? (o['result'] as Record<string, unknown>)
      : null;
  const userRaw = o['userMessage'] ?? o['user_message'] ?? nested?.['userMessage'] ?? nested?.['user_message'];
  const userMessage = normalizeAgenticAiMessageFromApi(userRaw);
  if (!userMessage) {
    return null;
  }
  const assistantRaw =
    o['assistantMessage'] ??
    o['assistant_message'] ??
    nested?.['assistantMessage'] ??
    nested?.['assistant_message'];
  const assistantMessage = normalizeAgenticAiMessageFromApi(assistantRaw) ?? undefined;
  return { userMessage, assistantMessage };
}

export function resolveBffChatConversationId(res: AgentBffChatSseResult): string | null {
  const o = res as Record<string, unknown>;
  const raw = o['conversationId'] ?? o['conversation_id'];
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw.trim();
  }
  return null;
}

/** BFF `event: error` JSON (e.g. `INVALID_REQUEST` after prompt scan) — may include `conversationId` for URL sync. */
export interface AgentBffChatSseErrorPayload {
  message: string;
  status?: number;
  conversationId?: string;
  code?: string;
  requestId?: string;
  type?: string;
  firestoreConversationId?: string;
  details?: unknown;
  envelopeVersion?: number;
  outcome?: 'ok' | 'domain_error' | 'validation_error' | 'upstream_error';
  displayText?: string;
}

/** BFF / Domain codes when the user cannot send prompts due to token quota. */
export const AGENTIC_AI_QUOTA_BLOCKED_CODES = new Set([
  'AGENTIC_AI_COMPANY_QUOTA_EXCEEDED',
  'AGENTIC_AI_QUOTA_EXCEEDED',
  'AGENTIC_AI_NO_USER_QUOTA',
  'AGENTIC_AI_UNAUTHORIZED_PROMPTS_EXCEEDED',
]);

export function isAgenticAiQuotaBlockedError(code: string | undefined | null): boolean {
  const c = code?.trim();
  return c != null && c.length > 0 && AGENTIC_AI_QUOTA_BLOCKED_CODES.has(c);
}

/** User-facing assistant line for BFF `error` events — no `Error:` prefix for quota blocks. */
export function formatAgentBffChatErrorDisplayMessage(err: AgentBffChatSseErrorPayload): string {
  const fromEnvelope = err.displayText?.trim();
  const fromMessage = err.message?.trim();
  const text = fromEnvelope || fromMessage || 'Something went wrong. Please try again.';
  if (isAgenticAiQuotaBlockedError(err.code) || err.code === 'INVALID_REQUEST') {
    return text;
  }
  return text.startsWith('Error:') ? text : `Error: ${text}`;
}

/**
 * Parses `data` from BFF SSE `error` events so the UI can navigate when `conversationId` is present.
 */
export function parseAgentBffChatSseErrorData(rawData: string, httpStatus?: number): AgentBffChatSseErrorPayload {
  const fallback: AgentBffChatSseErrorPayload = {
    message: rawData?.trim() ? rawData.trim() : 'Stream error',
    status: httpStatus,
  };
  try {
    const o = JSON.parse(rawData.trim()) as Record<string, unknown>;
    const message =
      typeof o['message'] === 'string' && o['message'].length > 0 ? o['message'] : fallback.message;
    const cidRaw = o['conversationId'] ?? o['conversation_id'];
    const conversationId =
      typeof cidRaw === 'string' && cidRaw.trim().length > 0 ? cidRaw.trim() : undefined;
    const env = o['envelopeVersion'];
    const outv = o['outcome'];
    const disp = o['displayText'];
    const code = typeof o['code'] === 'string' ? o['code'] : undefined;
    let details = o['details'];
    if (isAgenticAiQuotaBlockedError(code)) {
      const quotaDetails: Record<string, unknown> =
        details != null && typeof details === 'object' && !Array.isArray(details)
          ? { ...(details as Record<string, unknown>) }
          : {};
      if (o['reason'] != null) {
        quotaDetails['reason'] = o['reason'];
      }
      if (o['remainingTokens'] != null) {
        quotaDetails['remainingTokens'] = o['remainingTokens'];
      }
      if (o['unauthorizedPromptAttempts'] != null) {
        quotaDetails['unauthorizedPromptAttempts'] = o['unauthorizedPromptAttempts'];
      }
      quotaDetails['quotaBlocked'] = true;
      details = quotaDetails;
    }
    return {
      message,
      status: httpStatus,
      conversationId,
      code,
      requestId: typeof o['requestId'] === 'string' ? o['requestId'] : undefined,
      type: typeof o['type'] === 'string' ? o['type'] : undefined,
      firestoreConversationId:
        typeof o['firestoreConversationId'] === 'string' ? o['firestoreConversationId'] : undefined,
      details,
      envelopeVersion: typeof env === 'number' && Number.isFinite(env) ? env : undefined,
      outcome:
        outv === 'ok' ||
        outv === 'domain_error' ||
        outv === 'validation_error' ||
        outv === 'upstream_error'
          ? outv
          : undefined,
      displayText: typeof disp === 'string' ? disp : undefined,
    };
  } catch {
    return { ...fallback, status: httpStatus };
  }
}

function asStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

/** Nested `result` from orchestrator (clarify, bq, etc.). */
export function getBffChatResultBlock(res: AgentBffChatSseResult): AgentBffChatResultBlock | null {
  const raw = (res as Record<string, unknown>)['result'];
  if (raw == null) {
    return null;
  }
  let r: unknown = raw;
  if (typeof raw === 'string') {
    try {
      r = JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof r !== 'object' || r === null || Array.isArray(r)) {
    return null;
  }
  return r as AgentBffChatResultBlock;
}

/** Domain draft types that should open the Notes compose panel. */
export function isNotesDraftResultType(value: unknown): boolean {
  const t = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return t === 'notes' || t === 'notes_bulk' || t === 'notes_draft';
}

/** BFF envelope / nested `result` is a generated notes draft (open notes panel, not clarify). */
export function isBffResultNotes(res: AgentBffChatSseResult): boolean {
  const o = res as Record<string, unknown>;
  const rk = typeof o['resultKind'] === 'string' ? o['resultKind'].trim().toLowerCase() : '';
  const rootType = typeof o['type'] === 'string' ? o['type'].trim().toLowerCase() : '';
  if (isNotesDraftResultType(rk) || isNotesDraftResultType(rootType)) {
    return true;
  }
  const block = getBffChatResultBlock(res);
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const bt = typeof (block as Record<string, unknown>)['type'] === 'string'
      ? String((block as Record<string, unknown>)['type']).trim().toLowerCase()
      : '';
    if (isNotesDraftResultType(bt)) {
      return true;
    }
  }
  return false;
}

/** BFF envelope / nested `result` is a task manager draft (open task panel, not clarify). */
export function isBffResultTaskManager(res: AgentBffChatSseResult): boolean {
  const o = res as Record<string, unknown>;
  const rk = typeof o['resultKind'] === 'string' ? o['resultKind'].trim().toLowerCase() : '';
  const rootType = typeof o['type'] === 'string' ? o['type'].trim().toLowerCase() : '';
  if (rk === 'task_manager' || rootType === 'task_manager') {
    return true;
  }
  const block = getBffChatResultBlock(res);
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const bt = typeof (block as Record<string, unknown>)['type'] === 'string'
      ? String((block as Record<string, unknown>)['type']).trim().toLowerCase()
      : '';
    if (bt === 'task_manager') {
      return true;
    }
  }
  const nested = o['result'];
  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    const nt = typeof (nested as Record<string, unknown>)['type'] === 'string'
      ? String((nested as Record<string, unknown>)['type']).trim().toLowerCase()
      : '';
    if (nt === 'task_manager') {
      return true;
    }
  }
  return false;
}

/** BFF envelope / nested `result` is a voice AI interview draft (open voice AI panel, not clarify). */
export function isBffResultVoiceAi(res: AgentBffChatSseResult): boolean {
  const o = res as Record<string, unknown>;
  const rk = typeof o['resultKind'] === 'string' ? o['resultKind'].trim().toLowerCase() : '';
  const rootType = typeof o['type'] === 'string' ? o['type'].trim().toLowerCase() : '';
  if (rk === 'voice_ai' || rootType === 'voice_ai' || rk === 'voice_ai_call' || rootType === 'voice_ai_call') {
    return true;
  }
  const block = getBffChatResultBlock(res);
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const bt = typeof (block as Record<string, unknown>)['type'] === 'string'
      ? String((block as Record<string, unknown>)['type']).trim().toLowerCase()
      : '';
    if (bt === 'voice_ai' || bt === 'voice_ai_call') {
      return true;
    }
  }
  const nested = o['result'];
  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    const nt = typeof (nested as Record<string, unknown>)['type'] === 'string'
      ? String((nested as Record<string, unknown>)['type']).trim().toLowerCase()
      : '';
    if (nt === 'voice_ai' || nt === 'voice_ai_call') {
      return true;
    }
  }
  return false;
}

/** BFF envelope / nested `result` is a generated email (open compose panel, not clarify). */
export function isBffResultEmail(res: AgentBffChatSseResult): boolean {
  const o = res as Record<string, unknown>;
  const rk = typeof o['resultKind'] === 'string' ? o['resultKind'].trim().toLowerCase() : '';
  const rootType = typeof o['type'] === 'string' ? o['type'].trim().toLowerCase() : '';
  if (rk === 'email' || rootType === 'email') {
    return true;
  }
  const block = getBffChatResultBlock(res);
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const bt = typeof (block as Record<string, unknown>)['type'] === 'string'
      ? String((block as Record<string, unknown>)['type']).trim().toLowerCase()
      : '';
    if (bt === 'email') {
      return true;
    }
  }
  return false;
}

function trimLowerEnvelopeField(v: unknown): string {
  return typeof v === 'string' ? v.trim().toLowerCase() : '';
}

export function readExecutionType(raw: Record<string, unknown> | null | undefined): string {
  if (!raw || typeof raw !== 'object') {
    return '';
  }
  return trimLowerEnvelopeField(raw['executionType'] ?? raw['execution_type']);
}

/** True when metadata or BFF envelope is `executionType: single_agent` with an email result. */
export function isSingleAgentDirectEmailMetadata(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
    return false;
  }
  if (readExecutionType(meta) !== 'single_agent') {
    return false;
  }
  if (
    trimLowerEnvelopeField(meta['type']) === 'email' ||
    trimLowerEnvelopeField(meta['resultKind']) === 'email'
  ) {
    return true;
  }
  const res = meta['result'];
  if (res != null && typeof res === 'object' && !Array.isArray(res)) {
    if (trimLowerEnvelopeField((res as Record<string, unknown>)['type']) === 'email') {
      return true;
    }
  }
  const display = resolveAssistantDisplayMetadata(meta);
  return trimLowerEnvelopeField(display?.type) === 'email';
}

export function isSingleAgentDirectEmailBffResult(res: AgentBffChatSseResult): boolean {
  if (readExecutionType(res as Record<string, unknown>) !== 'single_agent') {
    return false;
  }
  return isBffResultEmail(res);
}

/** True when BFF `result` is a direct submission or tag (pipeline) creation with row data. */
export function isBffResultCandidateMatching(res: AgentBffChatSseResult): boolean {
  const resObj = res as Record<string, unknown>;
  const topMeta = normalizeSubmissionResultAssistantMetadata(resObj);
  if (topMeta && submissionResultHasRows(topMeta)) {
    return true;
  }
  const meta = buildAssistantMetadataFromBffResult(res);
  return normalizeSubmissionResultAssistantMetadata(meta) != null;
}

/** True when BFF `result` is an interview schedule action/result with success/failed rows. */
export function isBffResultInterviewSchedule(res: AgentBffChatSseResult): boolean {
  const resObj = res as Record<string, unknown>;
  const topMeta = normalizeInterviewScheduleResultAssistantMetadata(resObj);
  if (topMeta && interviewScheduleResultHasRows(topMeta)) {
    return true;
  }
  const meta = buildAssistantMetadataFromBffResult(res);
  return normalizeInterviewScheduleResultAssistantMetadata(meta) != null;
}

/**
 * True when BFF result asks the user to pick submission/pipeline/interview pairs
 * (`*_choice_needed`) — opens the right-side choice panel instead of clarify chips.
 */
export function isBffResultCandidateMatchingChoice(res: AgentBffChatSseResult): boolean {
  const resObj = res as Record<string, unknown>;
  if (extractCandidateMatchingChoicePayload(resObj)) {
    return true;
  }
  const block = getBffChatResultBlock(res);
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    return extractCandidateMatchingChoicePayload(block as Record<string, unknown>) != null;
  }
  return false;
}

/** Email draft that still needs compose / workflow send — excludes single-agent auto-send. */
export function isBffResultEmailDraft(res: AgentBffChatSseResult): boolean {
  return isBffResultEmail(res) && !isSingleAgentDirectEmailBffResult(res);
}

export function isSingleAgentDirectEmailAssistantMessage(message: AgenticAiMessage): boolean {
  if (message.role !== 'assistant') {
    return false;
  }
  const raw =
    message.metadata != null && typeof message.metadata === 'object' && !Array.isArray(message.metadata)
      ? (message.metadata as Record<string, unknown>)
      : null;
  return isSingleAgentDirectEmailMetadata(raw);
}

/** Hide assistant rows for single-agent email sends (mail already delivered server-side). */
export function hideSingleAgentDirectEmailAssistantMessages(
  messages: AgenticAiMessage[],
): AgenticAiMessage[] {
  return messages.filter((message) => !isSingleAgentDirectEmailAssistantMessage(message));
}

/** True when BFF payload is a BigQuery tabular result (do not treat top-level `options` as clarify). */
export function isBffResultBigQuery(res: AgentBffChatSseResult): boolean {
  const block = getBffChatResultBlock(res);
  if (block?.type === 'bq') {
    return true;
  }
  const rootMeta = (res as Record<string, unknown>)['metadata'];
  if (rootMeta && typeof rootMeta === 'object' && !Array.isArray(rootMeta)) {
    return (rootMeta as { type?: string }).type === 'bq';
  }
  return false;
}

/** Clarify chips: `result.type === 'clarify'` with `options[]`, or top-level `options` (not for `bq`). */
export function extractBffChatClarifyOptions(res: AgentBffChatSseResult): string[] {
  if (
    isBffResultBigQuery(res) ||
    isBffResultEmail(res) ||
    isBffResultNotes(res) ||
    isBffResultTaskManager(res) ||
    isBffResultVoiceAi(res) ||
    isBffResultCandidateMatchingChoice(res)
  ) {
    return [];
  }
  const block = getBffChatResultBlock(res);
  if (block?.type === 'bq_error') {
    return [];
  }
  if (block?.type === 'clarify' && Array.isArray(block.options)) {
    return asStringArray(block.options);
  }
  const top = (res as Record<string, unknown>)['options'];
  if (Array.isArray(top)) {
    return asStringArray(top);
  }
  return [];
}

/**
 * When true, keep the BFF center panel (question + chips) after navigating to `/ai-agent/:id`.
 * Extend here for future continuation UIs (not only clarify).
 * Conversational `purpose: chat` replies typically have empty options and stay in the thread bubble.
 */
export function shouldShowBffContinuationPanel(res: AgentBffChatSseResult): boolean {
  return extractBffChatClarifyOptions(res).length > 0;
}

/** Minimum display columns on a single-row BQ result for task/notes-style details instead of table/compact. */
export const BQ_SINGLE_ROW_DETAILS_MIN_COLUMNS = 6;

function bqMetaDisplayColumnCount(meta: Record<string, unknown>, row: Record<string, unknown>): number {
  const cols = meta['columns'];
  if (Array.isArray(cols) && cols.length > 0) {
    const fromColumns = cols.filter(
      (c): c is string => typeof c === 'string' && !!c.trim() && !isBqEncryptedIdColumnKey(c),
    ).length;
    if (fromColumns > 0) {
      return fromColumns;
    }
  }
  return countBqDisplayRowKeys(row);
}

function bqSingleRowFromMeta(
  meta: Record<string, unknown>,
): Record<string, unknown> | null {
  const rawTc = meta['totalCount'] ?? meta['total_count'];
  if (rawTc == null || !Number.isFinite(Number(rawTc)) || Number(rawTc) !== 1) {
    return null;
  }
  const rows = meta['rows'];
  if (!Array.isArray(rows) || rows.length !== 1) {
    return null;
  }
  const row = rows[0];
  if (row == null || typeof row !== 'object' || Array.isArray(row)) {
    return null;
  }
  return row as Record<string, unknown>;
}

/** One row on the current page and a single-record result (not paginated multi-row). */
function bqIsSingleRecordResult(meta: Record<string, unknown>): boolean {
  const rows = meta['rows'];
  if (!Array.isArray(rows) || rows.length !== 1) {
    return false;
  }
  const rawTc = meta['totalCount'] ?? meta['total_count'];
  if (rawTc != null && Number.isFinite(Number(rawTc))) {
    return Number(rawTc) === 1;
  }
  const hasMore = meta['hasMore'] === true || meta['has_more'] === true;
  return !hasMore;
}

/**
 * True when BigQuery should render as inline **compact** (single scalar): `totalCount === 1`,
 * exactly one row in `rows`, and that row has exactly **one** key (one column).
 * Any other shape uses the "View full table" card + optional auto-open side panel.
 */
export function isBqSingleScalarRowCompact(meta: Record<string, unknown> | null | undefined): boolean {
  if (!meta || meta['type'] !== 'bq') {
    return false;
  }
  const row = bqSingleRowFromMeta(meta);
  return row != null && countBqDisplayRowKeys(row) === 1;
}

/**
 * True when a single-row BQ result should use the task/notes-style details grid
 * (one record, one row on the page, at least {@link BQ_SINGLE_ROW_DETAILS_MIN_COLUMNS} display columns).
 * Pass {@link resolveAssistantDisplayMetadata} output — raw envelopes nest `rows` under `result`.
 */
export function isBqSingleRowDetailsView(meta: Record<string, unknown> | null | undefined): boolean {
  if (!meta || meta['type'] !== 'bq') {
    return false;
  }
  if (!bqIsSingleRecordResult(meta)) {
    return false;
  }
  const rows = meta['rows'];
  if (!Array.isArray(rows) || rows.length !== 1) {
    return false;
  }
  const row = rows[0];
  if (row == null || typeof row !== 'object' || Array.isArray(row)) {
    return false;
  }
  return bqMetaDisplayColumnCount(meta, row as Record<string, unknown>) >= BQ_SINGLE_ROW_DETAILS_MIN_COLUMNS;
}

/**
 * Open the BQ artifact panel and collapse the left menu when the result needs the full table:
 * `metadata.presentation` is set, or `totalCount > 1`, or any returned row has more than one column.
 * Single-scalar results ({@link isBqSingleScalarRowCompact}) never auto-open.
 */
export function shouldAutoOpenBqSidePanel(meta: Record<string, unknown> | null | undefined): boolean {
  if (!meta || meta['type'] !== 'bq') {
    return false;
  }
  if (isBqSingleScalarRowCompact(meta)) {
    return false;
  }
  const pres = meta['presentation'];
  if (pres != null && typeof pres === 'object' && !Array.isArray(pres)) {
    return true;
  }
  const rawTc = meta['totalCount'];
  if (rawTc != null && Number.isFinite(Number(rawTc)) && Number(rawTc) > 1) {
    return true;
  }
  const rows = meta['rows'];
  if (!Array.isArray(rows)) {
    return false;
  }
  for (const r of rows) {
    if (r && typeof r === 'object' && !Array.isArray(r)) {
      if (countBqDisplayRowKeys(r as Record<string, unknown>) > 1) {
        return true;
      }
    }
  }
  return false;
}

/** Suggestions (e.g. BQ follow-ups): `result.suggestions` or top-level `suggestions`. */
export function extractBffChatSuggestions(res: AgentBffChatSseResult): string[] {
  const block = getBffChatResultBlock(res);
  if (block && Array.isArray(block.suggestions)) {
    return asStringArray(block.suggestions);
  }
  const top = (res as Record<string, unknown>)['suggestions'];
  return asStringArray(top);
}

/**
 * Human-readable assistant text. Handles orchestrator envelopes where clarify lives under `result`.
 */
export function extractBffChatReply(res: AgentBffChatSseResult): string {
  const o = res as Record<string, unknown>;
  const displayText = o['displayText'];
  if (typeof displayText === 'string' && displayText.trim()) {
    return displayText.trim();
  }
  const nested = getBffChatResultBlock(res);
  if (nested?.type === 'clarify') {
    const nr = nested as Record<string, unknown>;
    if (nested.question != null && String(nested.question).trim()) {
      return String(nested.question).trim();
    }
    const innerReply = nr['reply'];
    if (typeof innerReply === 'string' && innerReply.trim()) {
      return innerReply.trim();
    }
  }
  if (nested?.type === 'bq') {
    const nr = nested as Record<string, unknown>;
    const innerReply = nr['reply'];
    if (typeof innerReply === 'string' && innerReply.trim()) {
      return innerReply;
    }
  }
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const nr = nested as Record<string, unknown>;
    const tg = nr['taskGraph'];
    if (tg != null && typeof tg === 'object' && !Array.isArray(tg)) {
      const innerReply = nr['reply'];
      if (typeof innerReply === 'string' && innerReply.trim()) {
        return innerReply.trim();
      }
    }
  }
  if (nested?.type === 'bq_error') {
    const nr = nested as Record<string, unknown>;
    const ir = nr['reply'];
    if (typeof ir === 'string' && ir.trim()) {
      return ir.trim();
    }
    const err = nr['error'];
    if (typeof err === 'string' && err.trim()) {
      return err.trim();
    }
  }
  if (nested?.type === 'text') {
    const nr = nested as Record<string, unknown>;
    const ans = nr['answer'] ?? nr['reply'];
    if (typeof ans === 'string' && ans.trim()) {
      return ans.trim();
    }
  }
  const v = o['reply'] ?? o['message'] ?? o['content'] ?? o['text'] ?? o['answer'];
  if (v != null) {
    return String(v);
  }
  if (nested != null && typeof nested['message'] === 'string') {
    return nested['message'];
  }
  return '';
}

function numOpt(v: unknown): number | undefined {
  if (v == null) {
    return undefined;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function stringArrayField(v: unknown): string[] {
  if (!Array.isArray(v)) {
    return [];
  }
  return v.filter((x): x is string => typeof x === 'string');
}

/**
 * Maps persisted `agentic_ai_conversation_messages.metadata` to {@link AgentChatMetadata} for the thread UI.
 * - Direct agentic-ai path: `type`, `rows`, `sql`, etc. live at the top level.
 * - Domain-orch path: same payload is under `metadata.result` (see Nest `cloneJsonForMetadata`).
 */
/** Prefer `metadata.confirmation` for BQ artifact titles when non-empty. */
export function resolveBqConfirmationTitle(
  meta: AgentChatMetadata | null | undefined,
  fallback: string,
): string {
  const c = meta?.confirmation;
  if (typeof c === 'string') {
    const t = c.trim();
    if (t.length > 0) {
      return t;
    }
  }
  return fallback;
}

export function resolveAssistantDisplayMetadata(
  raw: Record<string, unknown> | null | undefined,
): AgentChatMetadata | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  if (raw['source'] === 'domain-orch') {
    const res = raw['result'];
    if (res && typeof res === 'object' && !Array.isArray(res)) {
      const topType = typeof raw['type'] === 'string' ? String(raw['type']).trim() : '';
      if (topType === 'submission_result' || topType === 'pipeline_result' || topType === 'interview_schedule_result') {
        const merged = { ...(res as Record<string, unknown>), ...raw };
        return mapDomainOrchEnvelopeToDisplayMeta(raw, merged);
      }
      return mapDomainOrchEnvelopeToDisplayMeta(raw, res as Record<string, unknown>);
    }
    return null;
  }

  /** BQ payload only under `result` (no top-level `type`) — e.g. some persist/merge paths after pagination. */
  const nestedBq = raw['result'];
  if (
    nestedBq &&
    typeof nestedBq === 'object' &&
    !Array.isArray(nestedBq) &&
    (nestedBq as Record<string, unknown>)['type'] === 'bq'
  ) {
    return mapDomainOrchEnvelopeToDisplayMeta(raw, nestedBq as Record<string, unknown>);
  }

  /** Task graph: `result.taskGraph` without inner `type: 'bq'`. */
  if (
    nestedBq &&
    typeof nestedBq === 'object' &&
    !Array.isArray(nestedBq) &&
    (nestedBq as Record<string, unknown>)['type'] !== 'bq'
  ) {
    const innerTg = (nestedBq as Record<string, unknown>)['taskGraph'];
    if (innerTg != null && typeof innerTg === 'object' && !Array.isArray(innerTg)) {
      const tg = innerTg as Record<string, unknown>;
      const confirmation =
        typeof tg['confirmation'] === 'string' && tg['confirmation'].toString().trim()
          ? String(tg['confirmation']).trim()
          : typeof raw['displayText'] === 'string' && raw['displayText'].toString().trim()
            ? String(raw['displayText']).trim()
            : typeof (nestedBq as Record<string, unknown>)['reply'] === 'string'
              ? String((nestedBq as Record<string, unknown>)['reply']).trim()
              : undefined;
      const meta: AgentChatMetadata = {
        type: 'task_graph',
        taskGraph: tg as unknown as TaskGraphDto,
        ...(confirmation ? { confirmation } : {}),
      };
      return meta;
    }
  }

  /**
   * BQ under `result` without `type: 'bq'` (metadata split: top-level `type` + `rows`, extras in `result`).
   */
  if (nestedBq && typeof nestedBq === 'object' && !Array.isArray(nestedBq)) {
    const nb = nestedBq as Record<string, unknown>;
    if (
      nb['taskGraph'] == null &&
      typeof nb['sql'] === 'string' &&
      Array.isArray(nb['rows']) &&
      (raw['type'] === 'bq' || raw['resultKind'] === 'bq')
    ) {
      return mapDomainOrchEnvelopeToDisplayMeta(raw, { ...nb, type: 'bq' });
    }
    if (nb['type'] === 'bq_error') {
      const err =
        typeof nb['error'] === 'string'
          ? nb['error'].trim()
          : typeof nb['bigQueryError'] === 'string'
            ? nb['bigQueryError'].trim()
            : '';
      return {
        ...raw,
        type: 'bq_error',
        sql: typeof nb['sql'] === 'string' ? nb['sql'] : undefined,
        error: err || undefined,
        bigQueryError: typeof nb['bigQueryError'] === 'string' ? nb['bigQueryError'] : undefined,
        suggestions: stringArrayField(nb['suggestions'] ?? raw['suggestions']),
        sqlAfterFix:
          typeof nb['sqlAfterFix'] === 'string'
            ? nb['sqlAfterFix']
            : typeof raw['sqlAfterFix'] === 'string'
              ? raw['sqlAfterFix']
              : undefined,
        confirmation:
          typeof nb['confirmation'] === 'string' && nb['confirmation'].trim()
            ? nb['confirmation'].trim()
            : typeof raw['confirmation'] === 'string' && String(raw['confirmation']).trim()
              ? String(raw['confirmation']).trim()
              : undefined,
      } as AgentChatMetadata;
    }
  }

  if (typeof raw['type'] === 'string') {
    const m = raw as AgentChatMetadata;
    if (
      m.type === 'submission_result' ||
      m.type === 'submission_action' ||
      m.type === 'pipeline_result' ||
      m.type === 'pipeline_action' ||
      m.type === 'interview_schedule_result' ||
      m.type === 'interview_schedule_action'
    ) {
      const preview = mapDomainOrchEnvelopeToDisplayMeta(raw, raw);
      if (preview) {
        return preview;
      }
    }
    if (m.type === 'task_graph') {
      let next: AgentChatMetadata = { ...m };
      const existing = m.taskGraph;
      if (!existing || typeof existing !== 'object') {
        const inner = raw['result'];
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          const lifted = (inner as Record<string, unknown>)['taskGraph'];
          if (lifted != null && typeof lifted === 'object' && !Array.isArray(lifted)) {
            next = { ...next, taskGraph: lifted as unknown as TaskGraphDto };
          }
        }
      }
      if (!next.confirmation || String(next.confirmation).trim() === '') {
        const liftedTg = next.taskGraph as Record<string, unknown> | undefined;
        const c =
          liftedTg &&
          typeof liftedTg['confirmation'] === 'string' &&
          String(liftedTg['confirmation']).trim()
            ? String(liftedTg['confirmation']).trim()
            : typeof raw['displayText'] === 'string' && raw['displayText'].toString().trim()
              ? String(raw['displayText']).trim()
              : undefined;
        if (c) {
          next = { ...next, confirmation: c };
        }
      }
      return next;
    }
    if (m.type === 'bq') {
      let next: AgentChatMetadata = m;
      const innerLive =
        raw['result'] && typeof raw['result'] === 'object' && !Array.isArray(raw['result'])
          ? (raw['result'] as Record<string, unknown>)
          : null;
      if (innerLive) {
        const liveRows = innerLive['rows'];
        if (Array.isArray(liveRows) && liveRows.length > 0) {
          next = { ...next, rows: liveRows as Record<string, unknown>[] };
        }
        const liveCols = innerLive['columns'];
        if (Array.isArray(liveCols) && liveCols.every((c) => typeof c === 'string')) {
          next = { ...next, columns: liveCols as string[] };
        }
        if (typeof innerLive['sql'] === 'string' && String(innerLive['sql']).trim()) {
          next = { ...next, sql: String(innerLive['sql']) };
        }
      }
      if (m.confirmation == null || String(m.confirmation).trim() === '') {
        const inner = raw['result'];
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          const conf = (inner as Record<string, unknown>)['confirmation'];
          if (typeof conf === 'string' && conf.trim() !== '') {
            next = { ...next, confirmation: conf.trim() };
          }
        }
      }
      if (next.presentation == null) {
        const top = parseBqPresentationFromUnknown(raw['presentation']);
        if (top != null) {
          next = { ...next, presentation: top };
        } else {
          const inner = raw['result'];
          if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
            const innerPres = parseBqPresentationFromUnknown(
              (inner as Record<string, unknown>)['presentation'],
            );
            if (innerPres != null) {
              next = { ...next, presentation: innerPres };
            }
          }
        }
      }
      const topChart = raw['chartRows'];
      if (Array.isArray(topChart) && topChart.length > 0) {
        next = { ...next, chartRows: topChart as Record<string, unknown>[] };
      }
      const topChartTc = raw['chartTotalCount'];
      if (topChartTc != null && Number.isFinite(Number(topChartTc))) {
        next = { ...next, chartTotalCount: Number(topChartTc) };
      }
      const existingHc = next.hyperlink_columns;
      const hasHc = Array.isArray(existingHc) && existingHc.length > 0;
      if (!hasHc) {
        const innerRes = raw['result'];
        if (innerRes && typeof innerRes === 'object' && !Array.isArray(innerRes)) {
          const ir = innerRes as Record<string, unknown>;
          const liftedHc = parseHyperlinkColumns(ir['hyperlink_columns'] ?? ir['hyperlinkColumns']);
          if (liftedHc != null) {
            next = { ...next, hyperlink_columns: liftedHc };
          }
        }
      }
      const innerForSuggest =
        raw['result'] && typeof raw['result'] === 'object' && !Array.isArray(raw['result'])
          ? (raw['result'] as Record<string, unknown>)
          : undefined;
      if (next.presentation != null) {
        next = {
          ...next,
          presentation: bqPresentationForSuggestedOutputType(next.presentation, raw, innerForSuggest) ?? next.presentation,
        };
      }
      return next;
    }
    return m;
  }

  return null;
}

/** Tab order: chart first, then table, then compact. */
const BQ_VIEW_PREFERENCE_ORDER: readonly BqPresentationViewId[] = ['chart', 'table', 'compact'];

function isBqViewId(v: unknown): v is BqPresentationViewId {
  return v === 'table' || v === 'compact' || v === 'chart';
}

function parseBqAxisEncoding(raw: unknown): BqPresentationAxisEncoding | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const field = o['field'];
  if (typeof field !== 'string' || !field.trim()) {
    return undefined;
  }
  const label = o['label'];
  const enc: BqPresentationAxisEncoding = { field: field.trim() };
  if (typeof label === 'string' && label.trim() !== '') {
    enc.label = label.trim();
  }
  return enc;
}

function parseBqChartEncoding(raw: unknown):
  | NonNullable<BqPresentationChartHint['encoding']>
  | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const x = parseBqAxisEncoding(o['x']);
  const y = parseBqAxisEncoding(o['y']);
  const z = parseBqAxisEncoding(o['z']);
  if (x == null && y == null && z == null) {
    return undefined;
  }
  return { ...(x != null ? { x } : {}), ...(y != null ? { y } : {}), ...(z != null ? { z } : {}) };
}

function parseBqChartHint(raw: unknown): BqPresentationChartHint | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const kind = o['kind'];
  const k =
    kind === 'pie' || kind === 'doughnut' || kind === 'bar' || kind === 'line' ? kind : undefined;
  if (!k) {
    return undefined;
  }
  const encoding = parseBqChartEncoding(o['encoding']);
  const lkRaw = o['labelKey'];
  const vkRaw = o['valueKey'];
  const countByLabel = o['countByLabel'] === true;
  const title = o['title'];
  const titleOpt =
    typeof title === 'string' && title.trim() !== '' ? { title: title.trim() as string } : {};

  if (countByLabel) {
    if (typeof lkRaw !== 'string' || !lkRaw.trim()) {
      return undefined;
    }
    return {
      kind: k,
      labelKey: lkRaw.trim(),
      countByLabel: true,
      ...(encoding != null ? { encoding } : {}),
      ...titleOpt,
    };
  }

  if (k === 'bar' || k === 'line') {
    const xf = encoding?.x?.field;
    const yf = encoding?.y?.field;
    if (xf != null && xf.trim() !== '' && yf != null && yf.trim() !== '') {
      return {
        kind: k,
        labelKey: xf.trim(),
        valueKey: yf.trim(),
        encoding,
        ...titleOpt,
      };
    }
    if (typeof lkRaw === 'string' && lkRaw.trim() && typeof vkRaw === 'string' && vkRaw.trim()) {
      const lk = lkRaw.trim();
      const vk = vkRaw.trim();
      return {
        kind: k,
        labelKey: lk,
        valueKey: vk,
        ...(encoding != null
          ? { encoding }
          : { encoding: { x: { field: lk }, y: { field: vk } } }),
        ...titleOpt,
      };
    }
    return undefined;
  }

  if (typeof lkRaw !== 'string' || !lkRaw.trim()) {
    return undefined;
  }
  const lk = lkRaw.trim();
  if (typeof vkRaw !== 'string' || !vkRaw.trim()) {
    return undefined;
  }
  return {
    kind: k,
    labelKey: lk,
    valueKey: vkRaw.trim(),
    ...(encoding != null ? { encoding } : {}),
    ...titleOpt,
  };
}

/**
 * Parses optional `presentation` on `result` or flat metadata (`version` 1 or 2).
 */
export function parseBqPresentationFromUnknown(raw: unknown): BqPresentation | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const verRaw = o['version'];
  if (verRaw != null) {
    const vn = Number(verRaw);
    if (vn !== 1 && vn !== 2) {
      return undefined;
    }
  }
  const version: 1 | 2 = Number(verRaw) === 2 ? 2 : 1;
  const allowedRaw = o['allowedViews'];
  const allowed: BqPresentationViewId[] = [];
  if (Array.isArray(allowedRaw)) {
    for (const x of allowedRaw) {
      if (isBqViewId(x) && !allowed.includes(x)) {
        allowed.push(x);
      }
    }
  }
  if (allowed.length === 0) {
    return undefined;
  }
  const dvRaw = o['defaultView'];
  const defaultView: BqPresentationViewId = isBqViewId(dvRaw) && allowed.includes(dvRaw)
    ? dvRaw
    : allowed[0];
  const chart = parseBqChartHint(o['chart']);
  const pres: BqPresentation = {
    version,
    defaultView,
    allowedViews: allowed,
    ...(chart != null ? { chart } : {}),
  };
  return pres;
}

function readSuggestedOutputTypeFromRecords(
  ...sources: Array<Record<string, unknown> | null | undefined>
): string | undefined {
  for (const src of sources) {
    if (!src || typeof src !== 'object' || Array.isArray(src)) {
      continue;
    }
    const v = src['suggested_output_type'] ?? src['suggestedOutputType'];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return undefined;
}

/**
 * When `suggested_output_type` / `suggestedOutputType` is `list`, drop the chart tab and chart hint
 * so the right panel shows table/compact only (matches list-style results).
 */
export function bqPresentationForSuggestedOutputType(
  presentation: BqPresentation | undefined,
  ...suggestedSources: Array<Record<string, unknown> | null | undefined>
): BqPresentation | undefined {
  if (!presentation) {
    return undefined;
  }
  const hint = readSuggestedOutputTypeFromRecords(...suggestedSources);
  if (hint == null || hint.toLowerCase() !== 'list') {
    return presentation;
  }
  const allowed = presentation.allowedViews.filter((v) => v !== 'chart');
  if (allowed.length === 0) {
    return presentation;
  }
  let defaultView: BqPresentationViewId = presentation.defaultView;
  if (!allowed.some((v) => v === defaultView)) {
    defaultView = allowed[0];
  }
  const { chart: _omitChart, ...rest } = presentation;
  return {
    ...rest,
    allowedViews: allowed,
    defaultView,
  };
}

/** Initial tab when loading a message with `presentation`. */
export function initialBqPresentationView(p: BqPresentation): BqPresentationViewId {
  if (p.allowedViews.includes(p.defaultView)) {
    return p.defaultView;
  }
  for (const v of BQ_VIEW_PREFERENCE_ORDER) {
    if (p.allowedViews.includes(v)) {
      return v;
    }
  }
  return p.allowedViews[0];
}

/**
 * Default tab: first match in {@link BQ_VIEW_PREFERENCE_ORDER} among {@link effectiveBqPresentationViews}.
 */
export function preferredBqPresentationView(
  presentation: BqPresentation,
  effectiveViews: BqPresentationViewId[],
): BqPresentationViewId {
  for (const v of BQ_VIEW_PREFERENCE_ORDER) {
    if (effectiveViews.includes(v)) {
      return v;
    }
  }
  const pick = initialBqPresentationView(presentation);
  if (effectiveViews.includes(pick)) {
    return pick;
  }
  return effectiveViews[0] ?? pick;
}

export type FormatBqScalarOptions = {
  /** BigQuery column key — used to detect date/datetime fields. */
  columnKey?: string;
  /** Company pattern from `config_company_settings.date_format` (localStorage). */
  companyDateFormat?: string;
};

/** BQ period tokens (underscore form) → Title Case labels for table / chart / compact cells. */
const BQ_PERIOD_TOKEN_LABELS: Readonly<Record<string, string>> = {
  previous_day: 'Previous Day',
  previous_week: 'Previous Week',
  previous_month: 'Previous Month',
  previous_year: 'Previous Year',
  previous_quarter: 'Previous Quarter',
  last_day: 'Last Day',
  last_week: 'Last Week',
  last_month: 'Last Month',
  last_year: 'Last Year',
  last_quarter: 'Last Quarter',
  this_day: 'This Day',
  this_week: 'This Week',
  this_month: 'This Month',
  this_year: 'This Year',
  this_quarter: 'This Quarter',
  current_day: 'Current Day',
  current_week: 'Current Week',
  current_month: 'Current Month',
  current_year: 'Current Year',
  current_quarter: 'Current Quarter',
  next_day: 'Next Day',
  next_week: 'Next Week',
  next_month: 'Next Month',
  next_year: 'Next Year',
  next_quarter: 'Next Quarter',
};

const BQ_PERIOD_TOKEN_RE =
  /^(previous|last|this|current|next)_(day|week|month|year|quarter)$/i;

function titleCaseUnderscoreToken(raw: string): string {
  return raw
    .split('_')
    .filter((s) => s.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function formatBqPeriodTokenForDisplay(raw: string): string | null {
  const key = raw.trim().toLowerCase().replace(/\s+/g, '_');
  if (!key) {
    return null;
  }
  const mapped = BQ_PERIOD_TOKEN_LABELS[key];
  if (mapped) {
    return mapped;
  }
  if (BQ_PERIOD_TOKEN_RE.test(key)) {
    return titleCaseUnderscoreToken(key);
  }
  return null;
}

/**
 * Single cell / axis label for BQ rows — avoids `[object Object]` when values are wrapped or nested objects.
 */
export function formatBqScalarForDisplay(value: unknown, options?: FormatBqScalarOptions): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    if (typeof value === 'string') {
      if (options?.companyDateFormat) {
        const formatted = formatBqDateScalarForDisplay(
          value,
          options.companyDateFormat,
          options.columnKey,
        );
        if (formatted != null) {
          return formatted;
        }
      }
      const periodLabel = formatBqPeriodTokenForDisplay(value);
      if (periodLabel != null) {
        return periodLabel;
      }
    }
    return String(value);
  }
  if (typeof value === 'bigint') {
    return String(value);
  }
  if (typeof value === 'object' && value !== null && 'value' in (value as object)) {
    return formatBqScalarForDisplay((value as { value?: unknown }).value, options);
  }
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return '—';
    }
  }
  return String(value);
}

function numericCellValue(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'bigint') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof v === 'object' && v !== null && 'value' in (v as object)) {
    return numericCellValue((v as { value?: unknown }).value);
  }
  return null;
}

/** Shared bar/line/pie slice colors for BQ chart presentation (client-only). */
export const CHART_PALETTE = [
  'rgba(59, 130, 246, 0.75)',
  'rgba(16, 185, 129, 0.75)',
  'rgba(245, 158, 11, 0.85)',
  'rgba(239, 68, 68, 0.75)',
  'rgba(139, 92, 246, 0.75)',
  'rgba(236, 72, 153, 0.75)',
  'rgba(20, 184, 166, 0.75)',
  'rgba(251, 146, 60, 0.85)',
];

const CHART_PALETTE_LEN = CHART_PALETTE.length;

/** HSL (0–360, 0–100, 0–100) to `rgba(...)` for amCharts `Color.fromCSS` (hex-only `fromString`). */
function hslToRgba(h: number, s: number, l: number, a: number): string {
  const S = s / 100;
  const L = l / 100;
  const C = (1 - Math.abs(2 * L - 1)) * S;
  const H = ((h % 360) + 360) % 360;
  const X = C * (1 - Math.abs((H / 60) % 2 - 1));
  const m = L - C / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (H < 60) {
    rp = C;
    gp = X;
  } else if (H < 120) {
    rp = X;
    gp = C;
  } else if (H < 180) {
    gp = C;
    bp = X;
  } else if (H < 240) {
    gp = X;
    bp = C;
  } else if (H < 300) {
    rp = X;
    bp = C;
  } else {
    rp = C;
    bp = X;
  }
  const r = Math.round((rp + m) * 255);
  const g = Math.round((gp + m) * 255);
  const b = Math.round((bp + m) * 255);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * Distinct fill for the i-th category/slice (0-based) out of `total` points.
 * Uses {@link CHART_PALETTE} when `total` is at most its length; for more categories,
 * generates evenly spaced hues so 20+ bars/slices stay distinguishable (no short modulo repeat).
 * Always returns `rgba(...)` (including generated colors) so the chart layer can use `Color.fromCSS`.
 */
export function chartPresentationFill(index: number, total: number): string {
  const t = Math.max(total, 1);
  if (t <= CHART_PALETTE_LEN) {
    return CHART_PALETTE[index % CHART_PALETTE_LEN]!;
  }
  const hue = Math.round((360 * index) / t) % 360;
  const sat = 58 + ((index % 4) * 7);
  const light = 46 + ((index % 3) * 6);
  return hslToRgba(hue, Math.min(sat, 90), Math.min(light, 58), 0.82);
}

/** Client-only render model for amCharts 5 (same inputs as legacy Chart.js builder). */
export type BqAmChartCartesianPoint = {
  category: string;
  value: number;
  /** Bar/column or bullet color; from {@link chartPresentationFill} when building config. */
  fill?: string;
};

export type BqAmChartCartesianRenderConfig = {
  kind: 'bar' | 'line';
  title?: string;
  xAxisTitle: string;
  yAxisTitle: string;
  valueLabel: string;
  data: BqAmChartCartesianPoint[];
};

export type BqAmChartRadialRenderConfig = {
  kind: 'pie' | 'doughnut';
  title?: string;
  showLegend: boolean;
  data: { category: string; value: number; fill: string }[];
};

export type BqAmChartRenderConfig = BqAmChartCartesianRenderConfig | BqAmChartRadialRenderConfig;

function cartesianXyFields(chart: BqPresentationChartHint): { xField: string; yField: string } | null {
  const enc = chart.encoding;
  if (enc?.x?.field && enc?.y?.field) {
    return { xField: enc.x.field, yField: enc.y.field };
  }
  const vk = chart.valueKey;
  if (vk != null && vk.trim() !== '') {
    return { xField: chart.labelKey, yField: vk.trim() };
  }
  return null;
}

/**
 * Builds amCharts-friendly config from `rows` + hint (pie/doughnut or bar/line).
 * Same eligibility rules as the former Chart.js builder; API wire shape unchanged.
 */
export function buildBqAmChartRenderConfigForPresentation(
  rows: Record<string, unknown>[],
  chart: BqPresentationChartHint,
): BqAmChartRenderConfig | null {
  if (rows.length === 0) {
    return null;
  }

  if (chart.kind === 'bar' || chart.kind === 'line') {
    const xy = cartesianXyFields(chart);
    if (xy == null) {
      return null;
    }
    const { xField, yField } = xy;
    const points: Array<{ category: string; value: number }> = [];
    for (const row of rows) {
      const nv = numericCellValue(row[yField]);
      if (nv == null) {
        return null;
      }
      points.push({
        category: formatBqScalarForDisplay(row[xField]),
        value: nv,
      });
    }
    const maxPoints = 500;
    const slice = points.slice(0, maxPoints);
    const sliceTotal = slice.length;
    const data: BqAmChartCartesianPoint[] = slice.map((p, i) => ({
      ...p,
      fill: chartPresentationFill(i, sliceTotal),
    }));
    const xTitle =
      chart.encoding?.x?.label != null && chart.encoding.x.label.trim() !== ''
        ? chart.encoding.x.label.trim()
        : xField;
    const yTitle =
      chart.encoding?.y?.label != null && chart.encoding.y.label.trim() !== ''
        ? chart.encoding.y.label.trim()
        : yField;
    const valueLabel = chart.encoding?.y?.label ?? chart.encoding?.y?.field ?? yField;
    const title = chart.title != null && chart.title.trim() !== '' ? chart.title.trim() : undefined;
    return {
      kind: chart.kind,
      title,
      xAxisTitle: xTitle,
      yAxisTitle: yTitle,
      valueLabel,
      data,
    };
  }

  let sliceLabels: string[] = [];
  let sliceValues: number[] = [];

  if (chart.countByLabel === true) {
    const map = new Map<string, number>();
    for (const row of rows) {
      const raw = row[chart.labelKey];
      const label = formatBqScalarForDisplay(raw);
      map.set(label, (map.get(label) ?? 0) + 1);
    }
    const entries = [...map.entries()].sort((a, b) => b[1] - a[1]);
    const maxSlices = 48;
    sliceLabels = entries.slice(0, maxSlices).map((e) => e[0]);
    sliceValues = entries.slice(0, maxSlices).map((e) => e[1]);
  } else {
    const vk = chart.valueKey;
    if (vk == null || vk.trim() === '') {
      return null;
    }
    const labels: string[] = [];
    const values: number[] = [];
    for (const row of rows) {
      const lv = row[chart.labelKey];
      const nv = numericCellValue(row[vk]);
      if (nv == null) {
        return null;
      }
      labels.push(formatBqScalarForDisplay(lv));
      values.push(nv);
    }
    const maxSlices = 48;
    sliceLabels = labels.slice(0, maxSlices);
    sliceValues = values.slice(0, maxSlices);
  }
  const radialTotal = sliceLabels.length;
  const radialData: { category: string; value: number; fill: string }[] = sliceLabels.map(
    (cat, i) => ({
      category: cat,
      value: sliceValues[i]!,
      fill: chartPresentationFill(i, radialTotal),
    }),
  );
  const title = chart.title != null && chart.title.trim() !== '' ? chart.title.trim() : undefined;
  return {
    kind: chart.kind === 'doughnut' ? 'doughnut' : 'pie',
    title,
    showLegend: true,
    data: radialData,
  };
}

/**
 * When the API omits `presentation.chart` but allows chart and there are exactly two columns,
 * treat the more-numeric column as the measure and the other as the category (pie).
 */
function inferChartHintFromTwoColumnRows(
  columnKeys: string[],
  rows: Record<string, unknown>[],
): BqPresentationChartHint | undefined {
  if (columnKeys.length !== 2 || rows.length === 0) {
    return undefined;
  }
  const [c0, c1] = columnKeys;
  let score0 = 0;
  let score1 = 0;
  for (const row of rows) {
    if (numericCellValue(row[c0]) != null) {
      score0++;
    }
    if (numericCellValue(row[c1]) != null) {
      score1++;
    }
  }
  if (score0 === 0 && score1 === 0) {
    return undefined;
  }
  const valueKey = score1 >= score0 ? c1 : c0;
  const labelKey = valueKey === c0 ? c1 : c0;
  return {
    kind: 'bar',
    labelKey,
    valueKey,
    encoding: { x: { field: labelKey }, y: { field: valueKey } },
  };
}

/**
 * Chart hint from the server, or inferred for two-column tabular results when chart is allowed.
 */
export function getEffectiveBqChartHint(
  presentation: BqPresentation | undefined,
  rows: Record<string, unknown>[],
  columnKeys: string[],
): BqPresentationChartHint | undefined {
  if (!presentation?.allowedViews?.includes('chart')) {
    return undefined;
  }
  if (presentation.chart) {
    return presentation.chart;
  }
  if (columnKeys.length !== 2 || rows.length === 0) {
    return undefined;
  }
  return inferChartHintFromTwoColumnRows(columnKeys, rows);
}

/**
 * Allowed views for the current `rows`, ordered **chart → table → compact**.
 * Chart is included when an amCharts render config builds (server hint or two-column inference).
 * When `chartRows` / `chartColumnKeys` are set, chart eligibility uses them instead of `rows`.
 */
export function effectiveBqPresentationViews(
  presentation: BqPresentation | undefined,
  rows: Record<string, unknown>[],
  columnKeys: string[],
  chartRows?: Record<string, unknown>[],
  chartColumnKeys?: string[],
): BqPresentationViewId[] {
  if (!presentation?.allowedViews?.length) {
    return [];
  }
  const cols = columnKeys;
  const eligible = new Set<BqPresentationViewId>();

  if (rows.length > 0 && cols.length > 0) {
    if (presentation.allowedViews.includes('table')) {
      eligible.add('table');
    }
    if (presentation.allowedViews.includes('compact')) {
      eligible.add('compact');
    }
  }

  const chartR =
    chartRows != null && chartRows.length > 0 ? chartRows : rows;
  const chartCols =
    chartColumnKeys != null && chartColumnKeys.length > 0 ? chartColumnKeys : columnKeys;
  const chartHint = getEffectiveBqChartHint(presentation, chartR, chartCols);
  if (chartHint != null && presentation.allowedViews.includes('chart')) {
    if (buildBqAmChartRenderConfigForPresentation(chartR, chartHint) != null) {
      eligible.add('chart');
    }
  }

  const out: BqPresentationViewId[] = [];
  for (const v of BQ_VIEW_PREFERENCE_ORDER) {
    if (eligible.has(v)) {
      out.push(v);
    }
  }
  return out;
}

function mergeUsageIntoMeta(
  envelope: Record<string, unknown>,
  target: AgentChatMetadata,
): void {
  const u = envelope['usage'];
  if (!u || typeof u !== 'object' || Array.isArray(u)) {
    return;
  }
  const o = u as Record<string, unknown>;
  if (target.prompt_tokens == null) {
    const v = numOpt(o['promptTokens'] ?? o['prompt_tokens']);
    if (v != null) {
      target.prompt_tokens = v;
    }
  }
  if (target.output_tokens == null) {
    const v = numOpt(o['completionTokens'] ?? o['output_tokens'] ?? o['completion_tokens']);
    if (v != null) {
      target.output_tokens = v;
    }
  }
  if (target.cached_tokens == null) {
    const v = numOpt(o['cachedTokens'] ?? o['cached_tokens']);
    if (v != null) {
      target.cached_tokens = v;
    }
  }
  if (target.model == null && typeof o['model'] === 'string' && o['model'].trim() !== '') {
    target.model = o['model'];
  }
}

function parseHyperlinkColumns(raw: unknown): HyperlinkColumnSpec[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const out: HyperlinkColumnSpec[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      continue;
    }
    const o = item as Record<string, unknown>;
    const colRaw = o['column'] ?? o['COLUMN'] ?? o['Column'] ?? o['field'] ?? o['FIELD'];
    const modRaw = o['module'] ?? o['MODULE'] ?? o['Module'];
    const col = typeof colRaw === 'string' ? colRaw.trim() : '';
    const mod = typeof modRaw === 'string' ? modRaw.trim() : '';
    if (col && mod) {
      out.push({ column: col, module: mod });
    }
  }
  return out.length ? out : undefined;
}

function readSubmissionListForDisplayMeta(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (item): item is Record<string, unknown> =>
      item != null && typeof item === 'object' && !Array.isArray(item),
  );
}

function readInterviewScheduleListForDisplayMeta(value: unknown): Record<string, unknown>[] {
  return readSubmissionListForDisplayMeta(value);
}

function mapDomainOrchEnvelopeToDisplayMeta(
  envelope: Record<string, unknown>,
  r: Record<string, unknown>,
): AgentChatMetadata | null {
  const uiFallback = stringArrayField(envelope['uiOptions']);
  const t = r['type'];

  if (t === 'clarify') {
    const opts = stringArrayField(r['options']);
    const purposeRaw = r['purpose'];
    const purpose =
      typeof purposeRaw === 'string' && purposeRaw.trim() !== ''
        ? purposeRaw.trim()
        : undefined;
    const meta: AgentChatMetadata = {
      type: 'clarify',
      options: opts.length > 0 ? opts : uiFallback,
      ...(purpose ? { purpose } : {}),
    };
    return meta;
  }

  if (
    t === 'interview_schedule_choice_needed' ||
    t === 'voice_ai_choice_needed' ||
    t === 'submission_choice_needed' ||
    t === 'pipeline_choice_needed'
  ) {
    const reply =
      (typeof r['reply'] === 'string' && r['reply'].trim()) ||
      (typeof r['question'] === 'string' && r['question'].trim()) ||
      (typeof envelope['displayText'] === 'string' && envelope['displayText'].trim()) ||
      '';
    return {
      type: t,
      resultKind: t,
      reply,
      question: typeof r['question'] === 'string' ? r['question'] : reply,
      result: { ...r },
    } as AgentChatMetadata;
  }

  const taskGraphRaw = r['taskGraph'];
  if (
    t === 'task_graph' ||
    (taskGraphRaw != null && typeof taskGraphRaw === 'object' && !Array.isArray(taskGraphRaw))
  ) {
    const tg =
      taskGraphRaw != null && typeof taskGraphRaw === 'object' && !Array.isArray(taskGraphRaw)
        ? (taskGraphRaw as Record<string, unknown>)
        : ({} as Record<string, unknown>);
    const confRaw =
      typeof tg['confirmation'] === 'string' && tg['confirmation'].toString().trim()
        ? String(tg['confirmation']).trim()
        : typeof r['confirmation'] === 'string'
          ? String(r['confirmation']).trim()
          : typeof envelope['confirmation'] === 'string'
            ? String(envelope['confirmation']).trim()
            : undefined;
    const meta: AgentChatMetadata = {
      type: 'task_graph',
      taskGraph: tg as unknown as TaskGraphDto,
      ...(confRaw ? { confirmation: confRaw } : {}),
    };
    mergeUsageIntoMeta(envelope, meta);
    return meta;
  }

  if (t === 'bq') {
    const confRaw =
      typeof r['confirmation'] === 'string'
        ? r['confirmation']
        : typeof envelope['confirmation'] === 'string'
          ? envelope['confirmation']
          : undefined;
    const pres = bqPresentationForSuggestedOutputType(
      parseBqPresentationFromUnknown(r['presentation']),
      r,
      envelope,
    );
    const chartRowsRaw = r['chartRows'];
    const chartRows =
      Array.isArray(chartRowsRaw) && chartRowsRaw.length > 0
        ? (chartRowsRaw as Record<string, unknown>[])
        : undefined;
    const chartTcRaw = r['chartTotalCount'];
    const chartTotalCount =
      chartTcRaw != null && Number.isFinite(Number(chartTcRaw)) ? Number(chartTcRaw) : undefined;
    const hyperlinkColumns = parseHyperlinkColumns(
      r['hyperlink_columns'] ??
        r['hyperlinkColumns'] ??
        envelope['hyperlink_columns'] ??
        envelope['hyperlinkColumns'],
    );
    const colsRaw = r['columns'];
    const columns =
      Array.isArray(colsRaw) && colsRaw.length > 0 && colsRaw.every((c) => typeof c === 'string')
        ? (colsRaw as string[])
        : undefined;
    const meta: AgentChatMetadata = {
      type: 'bq',
      sql: typeof r['sql'] === 'string' ? r['sql'] : undefined,
      rows: Array.isArray(r['rows']) ? (r['rows'] as Record<string, unknown>[]) : [],
      ...(columns != null ? { columns } : {}),
      hasMore: r['hasMore'] === true,
      page: numOpt(r['page']) ?? 0,
      pageSize: numOpt(r['pageSize']) ?? 20,
      totalCount:
        r['totalCount'] != null && Number.isFinite(Number(r['totalCount']))
          ? Number(r['totalCount'])
          : null,
      ...(chartRows != null ? { chartRows } : {}),
      ...(chartTotalCount !== undefined ? { chartTotalCount } : {}),
      suggestions: stringArrayField(r['suggestions']),
      model: typeof r['model'] === 'string' ? r['model'] : undefined,
      prompt_tokens: numOpt(r['prompt_tokens'] ?? r['promptTokens']),
      output_tokens: numOpt(r['output_tokens'] ?? r['outputTokens']),
      cached_tokens: numOpt(r['cached_tokens'] ?? r['cachedTokens']),
      confirmation: confRaw?.trim() ? confRaw.trim() : undefined,
      ...(pres != null ? { presentation: pres } : {}),
      ...(hyperlinkColumns != null ? { hyperlink_columns: hyperlinkColumns } : {}),
      ...(typeof r['purpose'] === 'string' && r['purpose'].trim() !== ''
        ? { purpose: r['purpose'].trim() }
        : {}),
      ...(typeof r['agent'] === 'string' && r['agent'].trim() !== ''
        ? { agent: r['agent'].trim() }
        : {}),
    };
    mergeUsageIntoMeta(envelope, meta);
    return meta;
  }

  if (t === 'bq_error') {
    return { ...(r as object) } as AgentChatMetadata;
  }

  if (
    t === 'submission_action' ||
    t === 'submission_result' ||
    t === 'pipeline_action' ||
    t === 'pipeline_result'
  ) {
    const isPipeline = t === 'pipeline_action' || t === 'pipeline_result';
    const newSubs = readSubmissionListForDisplayMeta(r['new_submissions']);
    const existingSubs = readSubmissionListForDisplayMeta(r['existing_submissions']);
    if (newSubs.length === 0 && existingSubs.length === 0) {
      return null;
    }
    const batchCopy = buildSubmissionBatchCopyFromCounts(
      newSubs.length,
      existingSubs.length,
      isPipeline ? 'pipeline' : 'submission',
      formatUniqueJobCodesFromSubmissionRows([...newSubs, ...existingSubs]),
    );
    const pickedMessage =
      typeof r['message'] === 'string' && r['message'].trim()
        ? r['message'].trim()
        : typeof r['ats_message'] === 'string' && r['ats_message'].trim()
          ? r['ats_message'].trim()
          : typeof r['responseMessage'] === 'string' && r['responseMessage'].trim()
            ? r['responseMessage'].trim()
            : typeof r['reply'] === 'string' && r['reply'].trim()
              ? r['reply'].trim()
              : '';
    const message =
      isStaleSubmissionBatchDefaultMessage(
        pickedMessage,
        isPipeline ? 'pipeline' : 'submission',
        newSubs.length,
        existingSubs.length,
      )
        ? batchCopy.message
        : pickedMessage || batchCopy.message;
    const summary =
      typeof r['summary'] === 'string' && r['summary'].trim()
        ? r['summary'].trim()
        : batchCopy.summary;
    const meta: AgentChatMetadata = {
      type: isPipeline ? 'pipeline_result' : 'submission_result',
      message,
      summary,
      responseMessage: message,
      ats_message: message,
      new_submissions: newSubs,
      existing_submissions: existingSubs,
    };
    mergeUsageIntoMeta(envelope, meta);
    return meta;
  }

  if (t === 'interview_schedule_action' || t === 'interview_schedule_result') {
    const success = readInterviewScheduleListForDisplayMeta(r['success_interview_schedules']);
    const failed = readInterviewScheduleListForDisplayMeta(r['failed_interview_schedules']);
    const skipped = readInterviewScheduleListForDisplayMeta(r['skipped_interview_schedules']);
    if (success.length === 0 && failed.length === 0 && skipped.length === 0) {
      return null;
    }
    const batchCopy = buildInterviewScheduleBatchCopyFromCounts(
      success.length,
      failed.length,
      skipped.length,
    );
    const message =
      typeof r['message'] === 'string' && r['message'].trim()
        ? r['message'].trim()
        : typeof r['ats_message'] === 'string' && r['ats_message'].trim()
          ? r['ats_message'].trim()
          : typeof r['responseMessage'] === 'string' && r['responseMessage'].trim()
            ? r['responseMessage'].trim()
            : typeof r['reply'] === 'string' && r['reply'].trim()
              ? r['reply'].trim()
              : batchCopy.message;
    const summary =
      typeof r['summary'] === 'string' && r['summary'].trim()
        ? r['summary'].trim()
        : batchCopy.summary;
    const meta: AgentChatMetadata = {
      type: 'interview_schedule_result',
      message,
      summary,
      responseMessage: message,
      ats_message: message,
      success_interview_schedules: success,
      failed_interview_schedules: failed,
      skipped_interview_schedules: skipped,
    };
    mergeUsageIntoMeta(envelope, meta);
    return meta;
  }

  if (t === 'text') {
    return {
      type: 'text',
      suggestions: stringArrayField(r['suggestions']),
    };
  }

  if (t === 'task_manager' || envelope['resultKind'] === 'task_manager') {
    return {
      type: 'task_manager',
      resultKind: 'task_manager',
      result: { ...r },
    } as AgentChatMetadata;
  }

  // Saved rows — do not treat as drafts (editable Create Task / Add Notes).
  if (t === 'task_manager_created' || t === 'task_sent') {
    return {
      ...r,
      type: t === 'task_sent' ? 'task_sent' : 'task_manager_created',
    } as AgentChatMetadata;
  }

  if (t === 'notes_sent' || t === 'notes_action') {
    return {
      ...r,
      type: 'notes_sent',
    } as AgentChatMetadata;
  }

  if (t === 'voice_ai_sent') {
    return {
      ...r,
      type: 'voice_ai_sent',
    } as AgentChatMetadata;
  }

  if (t === 'email' || envelope['resultKind'] === 'email') {
    return {
      type: 'email',
      resultKind: 'email',
      result: { ...r },
    } as AgentChatMetadata;
  }

  if (
    t === 'notes' ||
    t === 'notes_bulk' ||
    t === 'notes_draft' ||
    envelope['resultKind'] === 'notes' ||
    envelope['resultKind'] === 'notes_bulk' ||
    envelope['resultKind'] === 'notes_draft'
  ) {
    return {
      type: 'notes',
      resultKind: 'notes',
      result: { ...r },
    } as AgentChatMetadata;
  }

  if (
    t === 'voice_ai' ||
    t === 'voice_ai_call' ||
    envelope['resultKind'] === 'voice_ai' ||
    envelope['resultKind'] === 'voice_ai_call'
  ) {
    return {
      type: 'voice_ai',
      resultKind: 'voice_ai',
      result: { ...r },
    } as AgentChatMetadata;
  }

  const opts = stringArrayField(r['options']);
  const sug = stringArrayField(r['suggestions']);
  if (typeof r['question'] === 'string' && (opts.length > 0 || uiFallback.length > 0)) {
    const purposeRaw = r['purpose'];
    const purpose =
      typeof purposeRaw === 'string' && purposeRaw.trim() !== ''
        ? purposeRaw.trim()
        : undefined;
    return {
      type: 'clarify',
      options: opts.length > 0 ? opts : uiFallback,
      ...(purpose ? { purpose } : {}),
    };
  }

  /** Conversational clarify (`purpose: chat`) when type was omitted — reply/question, usually no chips. */
  if (
    typeof r['purpose'] === 'string' &&
    r['purpose'].trim() === 'chat' &&
    (typeof r['question'] === 'string' || typeof r['reply'] === 'string')
  ) {
    return {
      type: 'clarify',
      purpose: 'chat',
      options: opts.length > 0 ? opts : uiFallback,
    };
  }

  if (typeof r['sql'] === 'string' && Array.isArray(r['rows'])) {
    const synthetic = { ...r, type: 'bq' };
    return mapDomainOrchEnvelopeToDisplayMeta(envelope, synthetic as Record<string, unknown>);
  }

  if (sug.length > 0) {
    return { type: 'text', suggestions: sug };
  }

  return null;
}

/** True when persisted / BFF-built assistant `metadata` should render the task graph timeline. */
export function metadataRecordHasTaskGraph(
  meta: Record<string, unknown> | null | undefined,
): boolean {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
    return false;
  }
  if (meta['type'] === 'task_graph' || meta['resultKind'] === 'task_graph') {
    return true;
  }
  const r = meta['result'];
  if (r && typeof r === 'object' && !Array.isArray(r)) {
    const tg = (r as Record<string, unknown>)['taskGraph'];
    return tg != null && typeof tg === 'object' && !Array.isArray(tg);
  }
  return false;
}

/** Standard envelope fields from BFF `event: result` for local thread `metadata`. */
function canonicalFieldsFromBffResult(res: AgentBffChatSseResult): Record<string, unknown> {
  const o = res as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  const ev = o['envelopeVersion'];
  const oc = o['outcome'];
  const dt = o['displayText'];
  const rk = o['resultKind'];
  const et = o['executionType'] ?? o['execution_type'];
  if (typeof ev === 'number' && Number.isFinite(ev)) {
    out['envelopeVersion'] = ev;
  }
  if (
    oc === 'ok' ||
    oc === 'domain_error' ||
    oc === 'validation_error' ||
    oc === 'upstream_error'
  ) {
    out['outcome'] = oc;
  }
  if (typeof dt === 'string' && dt.trim() !== '') {
    out['displayText'] = dt.trim();
  }
  if (typeof rk === 'string' && rk.trim() !== '') {
    out['resultKind'] = rk.trim();
  }
  if (typeof et === 'string' && et.trim() !== '') {
    out['executionType'] = et.trim();
  }
  return out;
}

export function buildAssistantMetadataFromBffResult(res: AgentBffChatSseResult): Record<string, unknown> | null {
  const block = getBffChatResultBlock(res);
  const asRecord = (x: AgentBffChatResultBlock): Record<string, unknown> => x as Record<string, unknown>;
  const canon = canonicalFieldsFromBffResult(res);
  const resObj = res as Record<string, unknown>;
  const liftedCandidateMatchingMeta = normalizeSubmissionResultAssistantMetadata(resObj);
  if (liftedCandidateMatchingMeta && submissionResultHasRows(liftedCandidateMatchingMeta)) {
    return { ...liftedCandidateMatchingMeta, ...canon };
  }
  const liftedInterviewMeta = normalizeInterviewScheduleResultAssistantMetadata(resObj);
  if (liftedInterviewMeta && interviewScheduleResultHasRows(liftedInterviewMeta)) {
    return { ...liftedInterviewMeta, ...canon };
  }
  const resultKind = typeof resObj['resultKind'] === 'string' ? resObj['resultKind'].trim() : '';
  const envelopeType = typeof resObj['type'] === 'string' ? resObj['type'].trim() : '';
  const blk = block as Record<string, unknown> | null;
  const taskGraphObj =
    blk &&
    blk['taskGraph'] != null &&
    typeof blk['taskGraph'] === 'object' &&
    !Array.isArray(blk['taskGraph'])
      ? (blk['taskGraph'] as Record<string, unknown>)
      : null;

  if (resultKind === 'task_graph' || envelopeType === 'task_graph' || taskGraphObj) {
    const tg = taskGraphObj ?? {};
    const confFromTg =
      typeof tg['confirmation'] === 'string' && tg['confirmation'].toString().trim()
        ? String(tg['confirmation']).trim()
        : typeof resObj['displayText'] === 'string' && resObj['displayText'].toString().trim()
          ? String(resObj['displayText']).trim()
          : undefined;
    const oa = resObj['openAiCalls'];
    const firstCall =
      Array.isArray(oa) && oa.length > 0 && oa[0] && typeof oa[0] === 'object' && !Array.isArray(oa[0])
        ? (oa[0] as Record<string, unknown>)
        : null;
    const pt = firstCall ? numOpt(firstCall['prompt_tokens']) : undefined;
    const ot = firstCall ? numOpt(firstCall['output_tokens']) : undefined;
    const ct = firstCall ? numOpt(firstCall['cached_tokens']) : undefined;
    const modelStr = firstCall && typeof firstCall['model'] === 'string' ? firstCall['model'] : undefined;
    return {
      type: 'task_graph',
      taskGraph: tg,
      ...(confFromTg ? { confirmation: confFromTg } : {}),
      ...(blk ? { result: blk } : {}),
      ...(modelStr ? { model: modelStr } : {}),
      ...(pt != null ? { prompt_tokens: pt } : {}),
      ...(ot != null ? { output_tokens: ot } : {}),
      ...(ct != null ? { cached_tokens: ct } : {}),
      ...canon,
    };
  }

  if (resultKind === 'email' || envelopeType === 'email' || block?.type === 'email') {
    const b = blk ? ({ ...blk } as Record<string, unknown>) : {};
    return {
      type: 'email',
      result: b,
      ...canon,
    };
  }

  if (
    isNotesDraftResultType(resultKind) ||
    isNotesDraftResultType(envelopeType) ||
    isNotesDraftResultType(block?.type)
  ) {
    const b = blk ? ({ ...blk } as Record<string, unknown>) : {};
    return {
      // Normalize for UI panel openers; keep original draft type inside `result`.
      type: 'notes',
      resultKind: 'notes',
      result: b,
      ...canon,
    };
  }

  if (resultKind === 'task_manager' || envelopeType === 'task_manager' || block?.type === 'task_manager') {
    const b = blk ? ({ ...blk } as Record<string, unknown>) : {};
    return {
      type: 'task_manager',
      resultKind: 'task_manager',
      result: b,
      ...canon,
    };
  }

  if (
    resultKind === 'voice_ai' ||
    resultKind === 'voice_ai_call' ||
    envelopeType === 'voice_ai' ||
    envelopeType === 'voice_ai_call' ||
    block?.type === 'voice_ai' ||
    block?.type === 'voice_ai_call'
  ) {
    const b = blk ? ({ ...blk } as Record<string, unknown>) : {};
    return {
      type: 'voice_ai',
      resultKind: 'voice_ai',
      result: b,
      ...canon,
    };
  }

  if (block?.type === 'bq') {
    const b = asRecord(block);
    const conf =
      typeof b['confirmation'] === 'string' && b['confirmation'].trim() !== ''
        ? b['confirmation'].trim()
        : undefined;
    const pres = bqPresentationForSuggestedOutputType(
      parseBqPresentationFromUnknown(b['presentation']),
      b,
      resObj,
    );
    const cr = b['chartRows'];
    const chartRows =
      Array.isArray(cr) && cr.length > 0 ? (cr as Record<string, unknown>[]) : undefined;
    const chartTotalCount = numOpt(b['chartTotalCount']);
    const hyperlinkColumns = parseHyperlinkColumns(b['hyperlink_columns'] ?? b['hyperlinkColumns']);
    const colsRaw = b['columns'];
    const columns =
      Array.isArray(colsRaw) && colsRaw.length > 0 && colsRaw.every((c) => typeof c === 'string')
        ? (colsRaw as string[])
        : undefined;
    return {
      type: 'bq',
      sql: typeof b['sql'] === 'string' ? b['sql'] : undefined,
      rows: Array.isArray(b['rows']) ? b['rows'] : undefined,
      ...(columns != null ? { columns } : {}),
      page: numOpt(b['page']) ?? 0,
      pageSize: numOpt(b['pageSize']) ?? 20,
      hasMore: b['hasMore'] === true,
      totalCount: numOpt(b['totalCount']),
      ...(chartRows != null ? { chartRows } : {}),
      ...(chartTotalCount != null ? { chartTotalCount } : {}),
      suggestions: asStringArray(b['suggestions']),
      prompt_tokens: numOpt(b['prompt_tokens']) ?? numOpt(b['promptTokens']),
      output_tokens: numOpt(b['output_tokens']) ?? numOpt(b['outputTokens']),
      cached_tokens: numOpt(b['cached_tokens']) ?? numOpt(b['cachedTokens']),
      model: typeof b['model'] === 'string' ? b['model'] : undefined,
      ...(conf != null ? { confirmation: conf } : {}),
      ...(pres != null ? { presentation: pres } : {}),
      ...(hyperlinkColumns != null ? { hyperlink_columns: hyperlinkColumns } : {}),
      ...(typeof b['purpose'] === 'string' && b['purpose'].trim() !== ''
        ? { purpose: b['purpose'].trim() }
        : {}),
      ...(typeof b['agent'] === 'string' && b['agent'].trim() !== ''
        ? { agent: b['agent'].trim() }
        : {}),
      ...canon,
    };
  }

  if (block?.type === 'clarify') {
    const b = asRecord(block);
    const purposeRaw = b['purpose'];
    const purpose =
      typeof purposeRaw === 'string' && purposeRaw.trim() !== ''
        ? purposeRaw.trim()
        : undefined;
    return {
      type: 'clarify',
      question: b['question'] != null ? String(b['question']) : undefined,
      options: asStringArray(b['options']),
      ...(purpose ? { purpose } : {}),
      ...canon,
    };
  }

  const choicePayload =
    extractCandidateMatchingChoicePayload(blk ?? undefined) ??
    extractCandidateMatchingChoicePayload(resObj);
  if (choicePayload) {
    const out = choicePayload.output;
    const reply =
      (typeof out['reply'] === 'string' && out['reply'].trim()) ||
      (typeof out['question'] === 'string' && out['question'].trim()) ||
      (typeof resObj['displayText'] === 'string' && resObj['displayText'].trim()) ||
      '';
    const metaType =
      choicePayload.kind === 'interview_schedule_choice'
        ? 'interview_schedule_choice_needed'
        : choicePayload.kind === 'voice_ai_choice'
          ? 'voice_ai_choice_needed'
          : choicePayload.kind === 'pipeline_choice'
            ? 'pipeline_choice_needed'
            : 'submission_choice_needed';
    return {
      type: metaType,
      resultKind: metaType,
      reply,
      question: typeof out['question'] === 'string' ? out['question'] : reply,
      result: out,
      ...canon,
    };
  }

  if (block?.type === 'bq_error') {
    return { ...(asRecord(block) as object), ...canon } as Record<string, unknown>;
  }

  const isSubmissionResultType = (v: string): boolean =>
    v === 'submission_action' || v === 'submission_result';
  const isPipelineResultType = (v: string): boolean =>
    v === 'pipeline_action' || v === 'pipeline_result';
  const blockType = typeof block?.type === 'string' ? block.type : '';
  const hasSubmission =
    isSubmissionResultType(blockType) ||
    isSubmissionResultType(envelopeType) ||
    isSubmissionResultType(resultKind);
  const hasPipeline =
    isPipelineResultType(blockType) ||
    isPipelineResultType(envelopeType) ||
    isPipelineResultType(resultKind);
  if (hasSubmission || hasPipeline) {
    const src = blk ?? {};
    const newSubs = readSubmissionListForDisplayMeta(
      src['new_submissions'] ?? resObj['new_submissions'],
    );
    const existingSubs = readSubmissionListForDisplayMeta(
      src['existing_submissions'] ?? resObj['existing_submissions'],
    );
    if (newSubs.length > 0 || existingSubs.length > 0) {
      const isPipeline = hasPipeline && !hasSubmission;
      const batchCopy = buildSubmissionBatchCopyFromCounts(
        newSubs.length,
        existingSubs.length,
        isPipeline ? 'pipeline' : 'submission',
        formatUniqueJobCodesFromSubmissionRows([...newSubs, ...existingSubs]),
      );
      const pick = (k: string): string => {
        const fromBlock = typeof src[k] === 'string' ? (src[k] as string).trim() : '';
        if (fromBlock) {
          return fromBlock;
        }
        const fromRoot = typeof resObj[k] === 'string' ? (resObj[k] as string).trim() : '';
        return fromRoot;
      };
      const pickedMessage =
        pick('message') ||
        pick('ats_message') ||
        pick('responseMessage') ||
        pick('reply');
      const message = isStaleSubmissionBatchDefaultMessage(
        pickedMessage,
        isPipeline ? 'pipeline' : 'submission',
        newSubs.length,
        existingSubs.length,
      )
        ? batchCopy.message
        : pickedMessage || batchCopy.message;
      const summary = pick('summary') || batchCopy.summary;
      return {
        type: isPipeline ? 'pipeline_result' : 'submission_result',
        message,
        summary,
        responseMessage: message,
        ats_message: message,
        new_submissions: newSubs,
        existing_submissions: existingSubs,
        ...(blk ? { result: blk } : {}),
        ...canon,
      };
    }
  }

  const isInterviewResultType = (v: string): boolean =>
    v === 'interview_schedule_action' || v === 'interview_schedule_result';
  const hasInterview =
    isInterviewResultType(blockType) ||
    isInterviewResultType(envelopeType) ||
    isInterviewResultType(resultKind);
  if (hasInterview) {
    const src = blk ?? {};
    const success = readInterviewScheduleListForDisplayMeta(
      src['success_interview_schedules'] ?? resObj['success_interview_schedules'],
    );
    const failed = readInterviewScheduleListForDisplayMeta(
      src['failed_interview_schedules'] ?? resObj['failed_interview_schedules'],
    );
    const skipped = readInterviewScheduleListForDisplayMeta(
      src['skipped_interview_schedules'] ?? resObj['skipped_interview_schedules'],
    );
    if (success.length > 0 || failed.length > 0 || skipped.length > 0) {
      const batchCopy = buildInterviewScheduleBatchCopyFromCounts(
        success.length,
        failed.length,
        skipped.length,
      );
      const pick = (k: string): string => {
        const fromBlock = typeof src[k] === 'string' ? (src[k] as string).trim() : '';
        if (fromBlock) {
          return fromBlock;
        }
        const fromRoot = typeof resObj[k] === 'string' ? (resObj[k] as string).trim() : '';
        return fromRoot;
      };
      const message =
        pick('message') ||
        pick('ats_message') ||
        pick('responseMessage') ||
        pick('reply') ||
        batchCopy.message;
      const summary = pick('summary') || batchCopy.summary;
      return {
        type: 'interview_schedule_result',
        message,
        summary,
        responseMessage: message,
        ats_message: message,
        success_interview_schedules: success,
        failed_interview_schedules: failed,
        skipped_interview_schedules: skipped,
        ...(blk ? { result: blk } : {}),
        ...canon,
      };
    }
  }

  const rootMeta = (res as Record<string, unknown>)['metadata'];
  if (rootMeta && typeof rootMeta === 'object' && !Array.isArray(rootMeta)) {
    const m = rootMeta as Record<string, unknown>;
    if (m['type'] === 'bq') {
      return buildAssistantMetadataFromBffResult({
        ...res,
        result: m,
      } as AgentBffChatSseResult);
    }
    if (m['type'] === 'task_graph' || m['taskGraph'] != null) {
      return { ...m, ...canon } as Record<string, unknown>;
    }
  }

  return null;
}
